import React, { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { BarChart3, TrendingUp, TrendingDown, ShoppingBag, Users, Truck, Store } from "lucide-react";

function pence(p: number) { return `£${(p / 100).toFixed(2)}`; }
function fmtDate(iso: string) {
  return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}
function fmtDay(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}
function pct(current: number, previous: number) {
  if (!previous) return null;
  return Math.round(((current - previous) / previous) * 100);
}

type Summary = {
  orders: number; gmvPence: number; platformPence: number; upliftPence?: number; deliveryPence: number;
  cashOrders: number; cardOrders: number; newUsers: number;
  prevOrders: number; prevGmvPence: number;
};
type DailyRow = { day: string; orders: number; gmvPence: number; deliveryPence: number };
type StoreRow = { storeName: string; orders: number; gmvPence: number; deliveryPence: number };
type SalesData = { days: number; summary: Summary; daily: DailyRow[]; topStores: StoreRow[] };

type TopEvent = { event: string; count: number; uniqueUsers: number };
type TopScreen = { screen: string | null; count: number };
type RecentEvent = { id: number; event: string; screen: string | null; platform: string | null; town: string | null; createdAt: string };
type AnalyticsData = { days: number; topEvents: TopEvent[]; topScreens: TopScreen[]; recent: RecentEvent[] };

const DAYS_OPTIONS = [1, 7, 14, 30, 90];

function StatCard({ label, value, sub, icon, change }: {
  label: string; value: string; sub?: string; icon: React.ReactNode; change?: number | null;
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-100 p-4 flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{label}</span>
        <span className="text-slate-300">{icon}</span>
      </div>
      <div className="text-2xl font-bold text-slate-800">{value}</div>
      <div className="flex items-center gap-2">
        {sub && <span className="text-xs text-slate-400">{sub}</span>}
        {change != null && (
          <span className={`flex items-center gap-0.5 text-xs font-semibold ${change >= 0 ? "text-emerald-600" : "text-red-500"}`}>
            {change >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            {Math.abs(change)}% vs prev
          </span>
        )}
      </div>
    </div>
  );
}

/*
 * min-w-0 on each bar is load-bearing.
 *
 * A flex item defaults to min-width:auto and so will not shrink below its own
 * content, and each bar's date label is nowrap. At 90 days that forced the chart
 * — and with it the entire document — to roughly 3,200px, so every other element
 * rendered at viewport width against a band of dead space. `truncate` cannot fix
 * that on its own; the item has to be allowed to shrink first.
 */
function DailyChart({ rows }: { rows: DailyRow[] }) {
  const maxGmv = Math.max(...rows.map(r => r.gmvPence), 1);
  return (
    <div className="bg-white rounded-xl border border-slate-100 p-4">
      <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Daily GMV</div>
      <div className="flex items-end gap-1.5 h-28">
        {rows.map(r => (
          <div key={r.day} className="flex-1 min-w-0 flex flex-col items-center gap-1 group">
            <div className="relative w-full flex flex-col justify-end h-24">
              <div
                className="w-full bg-primary/80 rounded-t transition-all hover:bg-primary"
                style={{ height: `${(r.gmvPence / maxGmv) * 96}px` }}
                title={`${fmtDay(r.day)}: ${pence(r.gmvPence)} (${r.orders} orders)`}
              />
            </div>
            <span className="text-[10px] text-slate-400 truncate w-full text-center">{new Date(r.day).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminAnalytics() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [days, setDays] = useState(7);
  const [tab, setTab] = useState<"sales" | "stores" | "events" | "screens" | "recent">("sales");
  const [sales, setSales] = useState<SalesData | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  const token = () => localStorage.getItem("ayrshire_token") ?? "";

  const load = useCallback(async (d: number) => {
    setLoading(true);
    try {
      const [sr, ar] = await Promise.all([
        fetch(`/api/admin/sales?days=${d}`, { headers: { Authorization: `Bearer ${token()}` } }),
        fetch(`/api/admin/analytics?days=${d}`, { headers: { Authorization: `Bearer ${token()}` } }),
      ]);
      if (!sr.ok || !ar.ok) throw new Error("Failed");
      const [sd, ad] = await Promise.all([sr.json() as Promise<SalesData>, ar.json() as Promise<AnalyticsData>]);
      setSales(sd);
      setAnalytics(ad);
    } catch {
      toast({
        title: "Failed to load analytics",
        variant: "destructive",
        action: <ToastAction altText="Reload" onClick={() => window.location.reload()}>Reload</ToastAction>,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { void load(days); }, [load, days]);

  if (!user || user.role !== "admin") return <Redirect to="/" />;

  const s = sales?.summary;
  const orderChange = s ? pct(s.orders, s.prevOrders) : null;
  const gmvChange   = s ? pct(s.gmvPence, s.prevGmvPence) : null;
  const maxStoreGmv = sales?.topStores[0]?.gmvPence ?? 1;
  const maxEvents   = analytics?.topEvents[0]?.count ?? 1;

  const TABS: { key: typeof tab; label: string }[] = [
    { key: "sales",   label: "Sales" },
    { key: "stores",  label: "Top Stores" },
    { key: "events",  label: "Events" },
    { key: "screens", label: "Screens" },
    { key: "recent",  label: "Recent" },
  ];

  return (
    <AdminLayout current="/admin/analytics">
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <BarChart3 size={22} className="text-slate-600" />
            <h1 className="text-xl font-bold text-slate-800">Analytics</h1>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">Last</span>
            {DAYS_OPTIONS.map(d => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold border transition-colors ${days === d ? "bg-primary text-white border-primary" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-5 flex-wrap">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${tab === t.key ? "bg-slate-800 text-white border-slate-800" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-12 bg-slate-100 rounded animate-pulse" />
            ))}
          </div>
        ) : tab === "sales" ? (
          <div className="space-y-4">
            {/* KPI cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard
                label="Total Orders"
                value={s?.orders.toLocaleString() ?? "0"}
                icon={<ShoppingBag size={16} />}
                change={orderChange}
                sub={`${s?.cashOrders ?? 0} cash · ${s?.cardOrders ?? 0} card`}
              />
              <StatCard
                label="Gross Sales (GMV)"
                value={pence(s?.gmvPence ?? 0)}
                icon={<TrendingUp size={16} />}
                change={gmvChange}
              />
              <StatCard
                label="Delivery Fees"
                value={pence(s?.deliveryPence ?? 0)}
                icon={<Truck size={16} />}
              />
              <StatCard
                label="New Users"
                value={s?.newUsers.toLocaleString() ?? "0"}
                icon={<Users size={16} />}
              />
            </div>

            {/* Platform fee card */}
            <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-5 py-3 flex items-center gap-4">
              <div>
                <div className="text-xs font-semibold text-emerald-600 uppercase tracking-wide">Hub Revenue</div>
                <div className="text-2xl font-bold text-emerald-700">{pence((s?.platformPence ?? 0) + (s?.upliftPence ?? 0))}</div>
              </div>
              <div className="text-xs text-emerald-500 ml-auto">Service fees {pence(s?.platformPence ?? 0)} + menu uplift {pence(s?.upliftPence ?? 0)} collected this period</div>
            </div>

            {/* Daily chart */}
            {sales && sales.daily.length > 0 && <DailyChart rows={sales.daily} />}

            {/* Daily table */}
            {sales && sales.daily.length > 0 && (
              <div className="rounded-xl border border-slate-200 overflow-hidden overflow-x-auto">
                <table className="w-full text-sm min-w-[480px]">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Day</th>
                      <th className="text-right px-4 py-3 font-semibold text-slate-600">Orders</th>
                      <th className="text-right px-4 py-3 font-semibold text-slate-600">GMV</th>
                      <th className="text-right px-4 py-3 font-semibold text-slate-600">Delivery Fees</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...sales.daily].reverse().map((r, idx) => (
                      <tr key={r.day} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                        <td className="px-4 py-2.5 text-slate-700 font-medium">{fmtDay(r.day)}</td>
                        <td className="px-4 py-2.5 text-slate-600 text-right">{r.orders}</td>
                        <td className="px-4 py-2.5 text-slate-800 font-semibold text-right">{pence(r.gmvPence)}</td>
                        <td className="px-4 py-2.5 text-slate-500 text-right">{pence(r.deliveryPence)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        ) : tab === "stores" ? (
          <div className="space-y-1.5">
            {sales?.topStores.map((s, idx) => (
              <div key={s.storeName} className="flex items-center gap-3 bg-white rounded-xl border border-slate-100 px-4 py-3">
                <span className="text-xs font-bold text-slate-400 w-5 text-right">{idx + 1}</span>
                <Store size={14} className="text-slate-400 shrink-0" />
                <div className="w-36 truncate text-sm font-medium text-slate-700">{s.storeName}</div>
                <div className="flex-1 relative h-5 bg-slate-50 rounded overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 bg-primary/20 rounded"
                    style={{ width: `${(s.gmvPence / maxStoreGmv) * 100}%` }}
                  />
                  <span className="absolute inset-y-0 left-2 flex items-center text-xs font-semibold text-primary">{pence(s.gmvPence)}</span>
                </div>
                <div className="text-xs text-slate-400 w-20 text-right">{s.orders} orders</div>
              </div>
            ))}
          </div>

        ) : tab === "events" && analytics ? (
          <div className="space-y-1.5">
            {analytics.topEvents.map(e => (
              <div key={e.event} className="flex items-center gap-3 bg-white rounded-xl border border-slate-100 px-4 py-2.5">
                <div className="w-48 truncate text-sm font-medium text-slate-700">{e.event}</div>
                <div className="flex-1 relative h-5 bg-slate-50 rounded overflow-hidden">
                  <div className="absolute inset-y-0 left-0 bg-primary/20 rounded" style={{ width: `${(e.count / maxEvents) * 100}%` }} />
                  <span className="absolute inset-y-0 left-2 flex items-center text-xs font-semibold text-primary">{e.count.toLocaleString()}</span>
                </div>
                <div className="text-xs text-slate-400 w-28 text-right">{e.uniqueUsers.toLocaleString()} users</div>
              </div>
            ))}
          </div>

        ) : tab === "screens" && analytics ? (
          <div className="space-y-1.5">
            {analytics.topScreens.map(s => (
              <div key={s.screen ?? "__none__"} className="flex items-center gap-3 bg-white rounded-xl border border-slate-100 px-4 py-2.5">
                <div className="w-48 truncate text-sm font-medium text-slate-700">{s.screen ?? "(none)"}</div>
                <div className="flex-1 relative h-5 bg-slate-50 rounded overflow-hidden">
                  <div className="absolute inset-y-0 left-0 bg-emerald-200 rounded" style={{ width: `${(s.count / (analytics.topScreens[0]?.count ?? 1)) * 100}%` }} />
                  <span className="absolute inset-y-0 left-2 flex items-center text-xs font-semibold text-emerald-700">{s.count.toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>

        ) : tab === "recent" && analytics ? (
          <div className="rounded-xl border border-slate-200 overflow-hidden overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Event</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Screen</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Platform</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Town</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Time</th>
                </tr>
              </thead>
              <tbody>
                {analytics.recent.map((e, idx) => (
                  <tr key={e.id} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                    <td className="px-4 py-2.5 font-medium text-slate-700">{e.event}</td>
                    <td className="px-4 py-2.5 text-slate-500">{e.screen ?? "—"}</td>
                    <td className="px-4 py-2.5 text-slate-500">{e.platform ?? "—"}</td>
                    <td className="px-4 py-2.5 text-slate-500">{e.town ?? "—"}</td>
                    <td className="px-4 py-2.5 text-slate-400 text-xs whitespace-nowrap">{fmtDate(e.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </AdminLayout>
  );
}
